import React, { useEffect, useState, useCallback } from 'react'
import { Alert, Row, Button } from 'reactstrap'
import { push } from 'connected-react-router'
import { useSelector, useDispatch } from 'react-redux'

// internal imports
import {
  searchNetSuiteAssets,
  clearNetSuiteAssets,
} from 'store/netSuiteAssetManager/operations'
import ErrorBoundary from 'components/ErrorBoundary'
import LicenseResultsGrid from './LicenseResultsGrid'
import Loading from 'components/Loading/Loading'
import SearchCriteriaBadges from 'components/SearchCriteriaBadges'
import SearchForm from './SearchForm'
import { Location, Match, NetSuiteAssetFilters, RootState } from 'types'

// TypeScript interfaces
interface NetSuiteAssetSearchProps {
  location: Location
  match: Match
}

const LicenseSearch: React.FC<NetSuiteAssetSearchProps> = ({
  match,
}: NetSuiteAssetSearchProps) => {
  const dispatch = useDispatch()

  // Redux state
  const { error, isLoading, netSuiteAssets } = useSelector(
    (state: RootState) => state.netSuiteAssetManager,
  )

  // Action creators
  const pushRoute = useCallback(
    (path: string) => {
      dispatch(push(path))
    },
    [dispatch],
  )

  const handleNetSuiteAssets = useCallback(
    (criteria: NetSuiteAssetFilters) => {
      dispatch(searchNetSuiteAssets(criteria))
    },
    [dispatch],
  )

  const clearLicenses = useCallback(() => {
    dispatch(clearNetSuiteAssets())
  }, [dispatch])

  useEffect(() => {
    if (netSuiteAssets) {
      const netSuiteAssetIds = Object.keys(netSuiteAssets)
      const isOneNetSuiteAsset = netSuiteAssetIds.length === 1
      if (isOneNetSuiteAsset) {
        const targetPath = `${match.path}/${netSuiteAssetIds[0]}`
        pushRoute(targetPath)
      }
    }
  }, [netSuiteAssets, match.path, pushRoute])

  // Local state
  const [criteria, setCriteria] = useState<NetSuiteAssetFilters>({})

  // Helper functions
  const removeProperty = useCallback(
    (key: string, object: NetSuiteAssetFilters): NetSuiteAssetFilters => {
      const updatedObject = { ...object }
      if (key in updatedObject) {
        delete updatedObject[key]
      }
      return updatedObject
    },
    [],
  )

  const addProperty = useCallback(
    (
      key: string,
      value: string | number,
      object: NetSuiteAssetFilters,
    ): NetSuiteAssetFilters => {
      return { ...object, [key]: value }
    },
    [],
  )

  // Event handlers
  const search = useCallback(() => {
    handleNetSuiteAssets(criteria)
  }, [criteria, handleNetSuiteAssets])

  const newSearch = useCallback(() => {
    setCriteria({})
    clearLicenses()
  }, [clearLicenses])

  const updateCriteria = useCallback(
    (key: string, value: string | number, doSearch: boolean = false) => {
      const updatedCriteria = !value
        ? removeProperty(key, criteria)
        : addProperty(key, value, criteria)

      setCriteria(updatedCriteria || {})

      if (Object.keys(updatedCriteria || {}).length === 0) {
        newSearch()
      }
      if (doSearch) {
        search()
      }
    },
    [criteria, removeProperty, addProperty, newSearch, search],
  )

  const selectLicense = useCallback(
    (netsuiteAssetId: string) => {
      pushRoute(`${match.path}/${netsuiteAssetId}`)
    },
    [match.path, pushRoute],
  )

  // Render logic
  const showSearchResults =
    netSuiteAssets && Object.keys(netSuiteAssets).length > 0

  return (
    <React.Fragment>
      {showSearchResults && (
        <div className="float-right">
          <Button color="primary" onClick={newSearch} outline>
            <i className="fas fa-search" />
            &nbsp;New Search
          </Button>
          {/* additional buttons */}
        </div>
      )}
      <h2>License Search</h2>
      <hr />
      <ErrorBoundary>
        <Loading isLoading={isLoading} message="Searching..." />
        {!showSearchResults && (
          <SearchForm
            clearSearch={newSearch}
            criteria={criteria}
            search={search}
            handleNetSuiteAssets={handleNetSuiteAssets}
            updateCriteria={updateCriteria}
          />
        )}
        {showSearchResults && (
          <div>
            <React.Fragment>
              <SearchCriteriaBadges
                clearSearch={newSearch}
                managerType={'netSuiteAssetManager'}
              />
              <hr />
            </React.Fragment>
            <Row>
              <LicenseResultsGrid
                netSuiteAssets={Object.values(netSuiteAssets)}
                selectNetSuiteAssets={selectLicense}
              />
            </Row>
          </div>
        )}
        {error && <Alert color="warning mt-3">Error: {error}</Alert>}
      </ErrorBoundary>
    </React.Fragment>
  )
}

export default LicenseSearch
